import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useIsBeamerEnabled } from './useIsBeamerEnabled'

export const BEAMER_TRIGGER_ID = 'beamer-trigger'

const staticConfig = {
  product_id: 'sbWaSPFO74505',
  button: false,
  mobile: false,
  feedback_button_position: 'right',
  selector: `#${BEAMER_TRIGGER_ID}`,
}

// Destroy Beamer when the route changes
const destroyBeamer = () => {
  window.Beamer?.destroy()
}

// Reinit Beamer when the route changes
const reinitBeamer = () => {
  window.Beamer?.init(staticConfig)
}

export const BeamerProvider = () => {
  const shouldEnableBeamer = useIsBeamerEnabled()
  const userData = useAuthenticatedUser()
  const router = useRouter()

  useEffect(() => {
    if (!shouldEnableBeamer) return

    const handleRouteChangeStart = () => destroyBeamer()
    const handleRouteChange = () => reinitBeamer()

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChange)
    router.events.on('routeChangeError', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChange)
      router.events.off('routeChangeError', handleRouteChange)
    }
  }, [router.events, shouldEnableBeamer])

  // Destroy Beamer when it should not be active: viewport went mobile, or the user
  // is no longer eligible (e.g. switched to a supplier session without a full reload).
  // Beamer's `mobile: false` config suppresses initial load on mobile but re-injects
  // nodes if the viewport resizes below the threshold at runtime; destroy() is the
  // only reliable cleanup.
  useEffect(() => {
    if (!shouldEnableBeamer) {
      destroyBeamer()
    }
  }, [shouldEnableBeamer])

  if (!shouldEnableBeamer) {
    return null
  }

  const handleBeamerLoad = () => {
    if (window.Beamer) {
      window.Beamer.update({
        user_id: userData.user.id,
      })
    }
  }

  return (
    <>
      <Script id="beamer-config">{`var beamer_config=${JSON.stringify(staticConfig)}`}</Script>
      <Script src="https://app.getbeamer.com/js/beamer-embed.js" onLoad={handleBeamerLoad} />
    </>
  )
}
